import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "Store/configureStore";

interface SelectorProps {
  organizationId: string;
}

const carbonEmissionsSelector = (state: RootState) => state.carbonEmissions;
const selectOrganizationId = (
  _: RootState,
  { organizationId }: SelectorProps
) => organizationId;

export const carbonEmissionsStateByOrganizationIdSelector = createSelector(
  carbonEmissionsSelector,
  selectOrganizationId,
  (state, organizationId) => state.byOrganizationId[organizationId]
);

export const carbonEmissionsDataByOrganizationIdSelector = createSelector(
  carbonEmissionsStateByOrganizationIdSelector,
  state => state?.data
);

export const carbonEmissionsIsLoadingByOrganizationIdSelector = createSelector(
  carbonEmissionsStateByOrganizationIdSelector,
  state => state?.isLoading || false
);

export const carbonEmissionsErrorByOrganizationIdSelector = createSelector(
  carbonEmissionsStateByOrganizationIdSelector,
  state => state?.error
);

export const carbonEmissionsPreviousYearTotalSelector = createSelector(
  carbonEmissionsDataByOrganizationIdSelector,
  data => data?.total.previousYear || 0
);

export const carbonEmissionsCurrentYearTotalSelector = createSelector(
  carbonEmissionsDataByOrganizationIdSelector,
  data => data?.total.currentYear || 0
);

export const carbonEmissionsMonthlyDataSelector = createSelector(
  carbonEmissionsDataByOrganizationIdSelector,
  data => data?.monthlyData || []
);

export const carbonEmissionsMonthlyDataForChartSelector = createSelector(
  [carbonEmissionsMonthlyDataSelector],
  (monthlyData): { label: string; value: number }[] => {
    // If there's no data, return empty array
    if (!monthlyData || monthlyData.length === 0) {
      return [];
    }

    // Sort the data by month (chronologically)
    const sortedData = [...monthlyData].sort((a, b) =>
      a.month.localeCompare(b.month)
    );

    // Transform data to the expected format
    const transformedData = sortedData.map(item => {
      // Convert YYYY-MM format to MMM format (e.g., "2023-01" to "Jan")
      let label: string;
      try {
        const [year, month] = item.month.split("-");
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);

        // Format as "MMM" (Jan, Feb, etc.)
        label = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
      } catch {
        // Fallback if parsing fails
        label = item.month;
      }

      return {
        label,
        value: item.value
      };
    });

    return transformedData;
  }
);
